<template>
  <FlotoForm
    ref="form"
    class="inline-block"
    layout="horizontal"
    @submit="moveToNextStep"
  >
    <FlotoDrawer
      ref="drawer"
      width="70%"
      :mask-closable="false"
      :closable="false"
      @show="handleShow"
    >
      <template v-slot:trigger="triggerSlotData">
        <slot name="trigger" v-bind="triggerSlotData" />
      </template>
      <template v-slot:title>
        <div class="flex flex-col">
          <slot name="title" />
          <MSteps :active="currentStep" :status="stepsProcess" class="my-2">
            <MStep>
              <template v-slot:title>
                <span class="text-color">
                  {{ $t('upload') }} {{ $t(data.importFileType) }}
                </span>
              </template>
            </MStep>
            <MStep>
              <template v-slot:title>
                <span class="text-color mx-2">
                  {{ $t('import_csv.map_fields') }}
                </span>
              </template>
            </MStep>
          </MSteps>
        </div>
      </template>
      <MRow>
        <MCol class="w-full">
          <!-- <MAlert
            v-if="data.success"
            :closable="false"
            class="my-2"
            banner
            type="success"
          >
            <div style="white-space: pre-line;">{{ data.success }}</div>
          </MAlert> -->
          <MAlert
            v-if="data.error"
            class="my-2"
            type="error"
            banner
            :closable="false"
          >
            <div v-for="m in data.error" :key="m" v-text="m" />
          </MAlert>
          <UploadStep
            v-if="currentStep === 0"
            v-model="data.file"
            :module-name="moduleName"
            :sub-module-name="subModuleName"
            :sample-file-link="sampleFileLink"
            :date-format.sync="data.dateFormat"
            :credential-type.sync="data.discoveryType"
            :ip-range-type.sync="data.ipRangeType"
            :cloud-type.sync="data.cloudType"
            :private-cloud-type.sync="data.privateCloudType"
            :snmp-field-type.sync="data.snmpFieldType"
            :asset-ci-type.sync="data.assetCiType"
            :service-catalog-id.sync="data.serviceCatalogId"
            :field-type="fieldType"
            :import-file-type.sync="data.importFileType"
            @change="isDirty = true"
          />
          <MappingStep
            v-if="currentStep === 1"
            v-model="data.columnMappings"
            :file-columns="data.headers"
            :resource-columns="availableColumns"
            :module-name="moduleName"
            @change="isDirty = true"
          />
          <!-- <MValidationProvider
            v-if="currentStep === 2"
            slim
            :name="$tc('row')"
            :rules="{ required: true, min: 1 }"
          >
            <template v-slot="{ errors }">
              <SelectionStep
                v-model="selectedRows"
                :columns="availableColumns"
                :table="data.table"
                @change="isDirty = true"
              />
              <div class="text-secondary-red">
                {{ errors && errors[0] }}
              </div>
            </template>
          </MValidationProvider> -->
        </MCol>
      </MRow>
      <template v-slot:actions="{ hide }">
        <MButton
          v-if="currentStep > 0"
          :disabled="processing"
          class="mx-1 inline-flex items-center"
          @click="moveToPreviousStep"
        >
          <MIcon :name="`chevron-${isRtl ? 'right' : 'left'}`" class="mx-1" />
          {{ $t('previous') }}
        </MButton>
        <MButton
          :disabled="!isCurrentStepValid"
          class="mx-1 inline-flex items-center"
          :loading="processing"
          @click="handleFormSubmit(hide)"
        >
          {{
            currentStep === 0 && data.importFileType !== 'mpp'
              ? $t('next')
              : submitButtonText
          }}
          <MIcon
            v-if="currentStep === 0 && data.importFileType !== 'mpp'"
            :name="`chevron-${isRtl ? 'left' : 'right'}`"
            class="mx-1"
          />
        </MButton>
        <!-- <MButton
          v-if="currentStep === 1"
          :disabled="!isCurrentStepValid"
          class="mr-2 inline-flex items-center"
          :loading="processing"
          @click="moveToNextStep(hide)"
        >
          {{ $tc('save') }} {{ $tc('batch') }}
        </MButton> -->
        <MButton
          class="mx-1"
          variant="default"
          :disabled="processing"
          @click="handleClose(hide)"
        >
          {{ $t('cancel') }}
        </MButton>
      </template>
    </FlotoDrawer>
    <template v-slot:submit>
      <span />
    </template>
  </FlotoForm>
</template>

<script>
import { getSupportDataApi, processStageApi } from './import-csv-api'
import UploadStep from './step-1'
import MappingStep from './step-2'
import { successToast } from '@motadata/ui'
import { authComputed } from '../../state/modules/auth'

// const stepMap = {
//   0: 'column_mapping',
//   1: 'save_batch',
// }

export default {
  name: 'ImportCsvContainer',
  components: {
    UploadStep,
    MappingStep,
  },
  props: {
    moduleName: { type: String, required: true },
    subModuleName: { type: String, default: undefined },
    userType: { type: String, default: undefined },
    refModel: { type: String, default: undefined },
    fieldId: { type: [Number, String], default: undefined },
    projectId: { type: [Number, String], default: undefined },
    submitButtonText: {
      type: String,
      default() {
        return this.$tc('import_csv.save_batch')
      },
    },
    additionalParams: {
      type: Object,
      default() {
        return {}
      },
    },
    fieldType: { type: [String], default: undefined },
  },
  data() {
    this.discoveryTypeMap = {
      wmi: 'wmi_credential',
      ssh: 'ssh_credential',
      snmp: 'snmp_credential',
      snmp_v3: 'snmp_credential',
      vmware: 'vmware_credential',
      aws: 'aws_credential',
      azure: 'azure_credential',
      nutanix: 'nutanix_credential',
    }
    return {
      processing: false,
      currentStep: 0,
      data: {
        file: null,
        dateFormat: 'dd-MM-yyyy HH:mm',
        discoveryType: this.$route.query.discoveryType,
        ipRangeType: this.$route.query.ipRangeType,
        cloudType: this.$route.query.cloudType,
        privateCloudType: this.$route.query.privateCloudType,
        snmpFieldType: this.$route.query.snmpFieldType,
        assetCiType: null,
        userType: null,
        refModel: null,
        fieldId: null,
        error: null,
        success: null,
        serviceCatalogId: null,
        importFileType: this.$route.query.importFileType || 'csv',
      },
      selectedRows: [],
      isDirty: false,
      availableColumns: [],
    }
  },
  computed: {
    ...authComputed,
    stepMap() {
      if (this.data.importFileType === 'mpp') {
        return {
          0: 'save_batch',
        }
      }
      return {
        0: 'column_mapping',
        1: 'save_batch',
      }
    },
    getCurrentModuleName() {
      if (this.moduleName === this.$constants.CREDENTIAL) {
        return this.discoveryTypeMap[this.data.discoveryType]
      }
      return this.moduleName
    },
    sampleFileLink() {
      let additionalParams = ''
      if (this.data.assetCiType) {
        additionalParams = `&assetCiType=${this.data.assetCiType}`
      }
      if (
        this.data.ipRangeType &&
        this.moduleName === this.$constants.IP_NETWORK
      ) {
        additionalParams = `&ipRangeType=${this.data.ipRangeType}`
      }
      if (
        this.data.cloudType &&
        this.moduleName === this.$constants.CLOUD_NETWORK
      ) {
        additionalParams = `&ipRangeType=${this.data.cloudType}`
      }
      if (
        this.data.privateCloudType &&
        this.moduleName === this.$constants.PRIVATE_CLOUD_NETWORK
      ) {
        additionalParams = `&ipRangeType=${this.data.privateCloudType}`
      }
      if (
        this.data.discoveryType &&
        this.moduleName === this.$constants.CREDENTIAL
      ) {
        additionalParams = `&ipRangeType=${this.data.discoveryType}`
      }
      if (
        this.data.snmpFieldType &&
        this.moduleName === this.$constants.SNMP_PROPERTY
      ) {
        additionalParams = `&ipRangeType=${this.data.snmpFieldType}`
      }
      if (this.userType) {
        additionalParams = `&userType=${this.userType}`
      }
      if (this.refModel) {
        additionalParams = `&refModel=${this.refModel}`
      }
      if (this.data.serviceCatalogId && this.refModel === 'service_catalog') {
        additionalParams = `&serviceCatalogId=${this.data.serviceCatalogId}`
      }
      let addiionalParamsStr = ''
      if (Object.keys(this.additionalParams).length) {
        addiionalParamsStr = Object.keys(this.additionalParams).reduce(
          (str, key) => {
            str += `&${key}=${this.additionalParams[key]}`
            return str
          },
          ''
        )
      }
      if (this.accessToken) {
        const module = this.getCurrentModuleName
        return `${this.isPortalLogin ? '/cportal' : ''}/download/${
          this.data.importFileType
        }/demo/${module}?dummy=true&${additionalParams}${addiionalParamsStr}`
      }
      return undefined
    },
    stepsProcess() {
      if (this.data.error) {
        return 'error'
      }
      if (this.data.success) {
        return 'finish'
      }
      return 'process'
    },
    isCurrentStepValid() {
      if (this.isDirty) {
        if (
          this.currentStep === 0 &&
          this.moduleName === this.$constants.SERVICE_CATALOG &&
          !this.data.serviceCatalogId
        ) {
          return false
        } else {
          return true
        }
      }
      if (this.data.error) {
        return false
      }
      if (this.currentStep === 0) {
        return this.validateUploadStep()
      }
      if (this.currentStep === 1) {
        return this.validateMappingStep()
      }
      return false
    },
  },
  watch: {
    'data.file': function () {
      this.selectedRows = []
    },
  },
  // created() {
  //   getSupportDataApi(this.moduleName).then((data) => {
  //     this.availableColumns = data
  //   })
  // },
  methods: {
    handleShow() {
      this.currentStep = 0
      this.isDirty = false
      this.selectedRows = []
      this.data = {
        file: null,
        dateFormat: 'dd-MM-yyyy HH:mm',
        discoveryType: this.$route.query.discoveryType,
        ipRangeType: this.$route.query.ipRangeType,
        cloudType: this.$route.query.cloudType,
        privateCloudType: this.$route.query.privateCloudType,
        snmpFieldType: this.$route.query.snmpFieldType,
        importFileType: this.$route.query.importFileType || 'csv',
        error: null,
        success: null,
      }
      this.$emit('trigger')
    },
    handleClose(hideFn) {
      if (this.data.success) {
        this.$emit('done')
      }
      hideFn()
    },
    validateUploadStep() {
      if (this.moduleName === this.$constants.ASSET) {
        if (this.data.file && this.data.assetCiType) {
          return true
        }
        return false
      }
      if (this.moduleName === this.$constants.SERVICE_CATALOG) {
        if (this.data.file && this.data.serviceCatalogId) {
          return true
        }
        return false
      } else {
        if (this.data.file) {
          return true
        }
        return false
      }
    },
    validateMappingStep() {
      // this will be handled by form validation
      return true
    },
    handleFormSubmit(hideFn) {
      if (this.currentStep === 0) {
        const additionalUrlParams = {}
        if (this.data.assetCiType) {
          additionalUrlParams.assetCiType = this.data.assetCiType
        }
        if (
          this.data.ipRangeType &&
          this.moduleName === this.$constants.IP_NETWORK
        ) {
          additionalUrlParams.ipRangeType = this.data.ipRangeType
        }
        if (
          this.data.cloudType &&
          this.moduleName === this.$constants.CLOUD_NETWORK
        ) {
          additionalUrlParams.ipRangeType = this.data.cloudType
        }
        if (
          this.data.privateCloudType &&
          this.moduleName === this.$constants.PRIVATE_CLOUD_NETWORK
        ) {
          additionalUrlParams.ipRangeType = this.data.privateCloudType
        }
        if (
          this.data.discoveryType &&
          this.moduleName === this.$constants.CREDENTIAL
        ) {
          additionalUrlParams.ipRangeType = this.data.discoveryType
        }
        if (
          this.data.snmpFieldType &&
          this.moduleName === this.$constants.SNMP_PROPERTY
        ) {
          additionalUrlParams.ipRangeType = this.data.snmpFieldType
        }
        if (this.userType) {
          additionalUrlParams.userType = this.userType
        }
        if (this.refModel) {
          additionalUrlParams.refModel = this.refModel
        }
        if (this.data.serviceCatalogId && this.refModel === 'service_catalog') {
          additionalUrlParams.serviceCatalogId = this.data.serviceCatalogId
        }
        if (this.data.importFileType !== 'mpp') {
          getSupportDataApi(this.getCurrentModuleName, additionalUrlParams)
            .then((data) => {
              this.availableColumns = data
            })
            .finally(() => {
              this.processing = false
            })
        }
      }
      this.$refs.form.submit(hideFn)
    },
    moveToNextStep() {
      let requestData = this.data
      if (this.userType) {
        requestData = {
          ...this.data,
          userType: this.userType,
        }
      }
      if (this.refModel) {
        requestData = {
          ...this.data,
          refModel: this.refModel,
        }
      }
      if (this.fieldId) {
        requestData = {
          ...this.data,
          fieldId: this.fieldId,
          refModel: this.refModel,
        }
      }
      if (this.fieldType) {
        requestData = {
          ...this.data,
          fieldType: this.fieldType,
        }
      }
      if (this.projectId) {
        requestData = {
          ...this.data,
          projectId: this.projectId,
        }
      }
      this.processing = true
      processStageApi(
        this.moduleName === this.$constants.CREDENTIAL
          ? this.discoveryTypeMap[this.data.discoveryType]
          : this.moduleName === this.$constants.ASSET
          ? this.subModuleName
          : this.moduleName,
        this.stepMap[this.currentStep],
        requestData,
        this.selectedRows
      )
        .then((data) => {
          this.data = data
          if (!data.error && !data.success) {
            this.currentStep += 1
            this.isDirty = false
          }
          if (data.batchId) {
            this.currentStep = null
            this.$emit('imported', data)
            successToast(
              this.$t('import_csv.save_batch_instruction', {
                batchNumber: data.batchNumber,
              })
            )
            this.$refs.drawer.hide()
          } else if (
            this.moduleName === this.$constants.DEPENDENT_VALUE &&
            data.tree
          ) {
            this.currentStep = null
            this.$emit('imported', data)
            this.$refs.drawer.hide()
          } else if (
            this.moduleName === this.$constants.SINGLE_COLUMN &&
            data.options
          ) {
            this.currentStep = null
            this.$emit('imported', data)
            this.$refs.drawer.hide()
          }
        })
        .finally(() => {
          this.processing = false
        })
    },
    moveToPreviousStep() {
      this.currentStep -= 1
    },
  },
}
</script>

<style lang="less" scoped>
.text-color {
  color: var(--page-text-color);
}
</style>
